import { colorFor } from './scale';
import { ageLabel } from './popup';
import { cardinal, isCurrent, type StationState } from './types';
import { matchesFilters } from './filters';

/**
 * The station list, ordered strongest-first — the fastest way to read
 * "where is it blowing hardest right now" without hunting across the map.
 */
export function renderPanel(
  host: HTMLElement,
  states: Map<string, StationState>,
  onSelect: (id: string) => void,
  selectedId: string | null = null,
  activeFilters: ReadonlySet<string> = new Set(),
): void {
  const rows = [...states.values()]
    .filter((s) => matchesFilters(s.station, activeFilters))
    .sort((a, b) => {
      const sa = isCurrent(a) ? a.latest!.speedKt : -1;
      const sb = isCurrent(b) ? b.latest!.speedKt : -1;
      return sb - sa;
    });

  host.textContent = '';

  if (rows.length === 0) {
    const empty = document.createElement('p');
    empty.className = 'list-empty';
    empty.textContent = 'No station reports all of those together.';
    host.append(empty);
    return;
  }

  const list = document.createElement('ul');
  list.className = 'station-list';

  for (const state of rows) {
    const { station, latest } = state;
    const usable = isCurrent(state);
    const stale = latest !== null && !usable;

    const item = document.createElement('li');
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'station-row';
    button.dataset.station = station.id;
    if (station.id === selectedId) {
      button.classList.add('is-selected');
      button.setAttribute('aria-current', 'true');
    }

    const dir = usable && latest!.dirDeg !== null ? cardinal(latest!.dirDeg) : '';
    const detail = usable
      ? `${dir}${latest!.gustKt !== null ? ` · gust ${Math.round(latest!.gustKt)}` : ''}`
      : state.loading
        ? 'loading…'
        : stale
          ? `stale · ${ageLabel(latest!.t)}`
          : 'no data';

    button.innerHTML = `
      <span class="row-dot" style="background:${usable ? colorFor(latest!.speedKt) : 'transparent'};
        ${station.kind === 'buoy' ? 'border-color:#c3c2b7' : ''}"></span>
      <span class="row-text">
        <span class="row-name">${station.name}</span>
        <span class="row-detail">${detail}</span>
      </span>
      <span class="row-speed">${usable ? latest!.speedKt.toFixed(0) : '–'}</span>`;

    button.addEventListener('click', () => onSelect(station.id));
    item.append(button);
    list.append(item);
  }

  host.append(list);
}
